import { BASE_AUTH_OFFICE_API, BASE_AUTH_OFFICE_API_V2 } from '@/core/api-config';
import { useOperatorStore } from '@/core/stores/operator-store';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import {
  ApiToken,
  ApiTokenRelation,
  ApiTokenEndpoint,
  CreateTokenRequest,
  UpdateTokenRequest,
  AddRelationRequest,
  AddEndpointRequest,
} from '../types/api-token-types';

interface ApiResponse {
  isSuccess?: boolean;
  IsSuccess?: boolean;
  errorMessage?: string;
  ErrorMessage?: string;
}

interface TokenListResponse extends ApiResponse {
  tokens?: ApiToken[];
  Tokens?: ApiToken[];
}

interface TokenResponse extends ApiResponse {
  token?: ApiToken;
  Token?: ApiToken;
}

interface CreateTokenResponse extends ApiResponse {
  tokenInfo?: ApiToken;
  TokenInfo?: ApiToken;
  token?: string;
  Token?: string;
}

interface DeleteResponse extends ApiResponse {
  deleted?: boolean;
}

interface RelationResponse extends ApiResponse {
  relation?: ApiTokenRelation;
  Relation?: ApiTokenRelation;
}

interface EndpointResponse extends ApiResponse {
  endpoint?: ApiTokenEndpoint;
  Endpoint?: ApiTokenEndpoint;
}

export class ApiTokensApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers = await this.tokenManager.getAuthHeaders();
    return { 'Content-Type': 'application/json', ...headers };
  }

  async getAllTokens(): Promise<{ isSuccess: boolean; tokens: ApiToken[]; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens`,
      { method: 'GET', headers },
      'ApiTokens',
      '/api-tokens'
    );
    const data: TokenListResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? true,
      tokens: data.tokens ?? data.Tokens ?? [],
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async createToken(request: CreateTokenRequest): Promise<{ isSuccess: boolean; tokenInfo: ApiToken | null; rawToken: string; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens`,
      { method: 'POST', headers, body: JSON.stringify(request) },
      'ApiTokens',
      '/api-tokens'
    );
    const data: CreateTokenResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? false,
      tokenInfo: data.tokenInfo ?? data.TokenInfo ?? null,
      rawToken: data.token ?? data.Token ?? '',
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async updateToken(tokenId: number, request: UpdateTokenRequest): Promise<{ isSuccess: boolean; token: ApiToken | null; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens/${tokenId}`,
      { method: 'PATCH', headers, body: JSON.stringify(request) },
      'ApiTokens',
      `/api-tokens/${tokenId}`
    );
    const data: TokenResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? false,
      token: data.token ?? data.Token ?? null,
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async deleteToken(tokenId: number): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens/${tokenId}`,
      { method: 'DELETE', headers },
      'ApiTokens',
      `/api-tokens/${tokenId}`
    );
    const data: DeleteResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? false,
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async addRelation(tokenId: number, request: AddRelationRequest): Promise<{ isSuccess: boolean; relation: ApiTokenRelation | null; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens/${tokenId}/relations`,
      { method: 'POST', headers, body: JSON.stringify(request) },
      'ApiTokens',
      `/api-tokens/${tokenId}/relations`
    );
    const data: RelationResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? false,
      relation: data.relation ?? data.Relation ?? null,
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async deleteRelation(tokenId: number, relationId: number): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens/${tokenId}/relations/${relationId}`,
      { method: 'DELETE', headers },
      'ApiTokens',
      `/api-tokens/${tokenId}/relations/${relationId}`
    );
    const data: DeleteResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? false,
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async addEndpoint(tokenId: number, request: AddEndpointRequest): Promise<{ isSuccess: boolean; endpoint: ApiTokenEndpoint | null; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens/${tokenId}/endpoints`,
      { method: 'POST', headers, body: JSON.stringify(request) },
      'ApiTokens',
      `/api-tokens/${tokenId}/endpoints`
    );
    const data: EndpointResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? false,
      endpoint: data.endpoint ?? data.Endpoint ?? null,
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async deleteEndpoint(tokenId: number, endpointId: number): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/api-tokens/${tokenId}/endpoints/${endpointId}`,
      { method: 'DELETE', headers },
      'ApiTokens',
      `/api-tokens/${tokenId}/endpoints/${endpointId}`
    );
    const data: DeleteResponse = await response.json();
    return {
      isSuccess: data.isSuccess ?? data.IsSuccess ?? false,
      errorMessage: data.errorMessage ?? data.ErrorMessage,
    };
  }

  async getDistributors(): Promise<Array<{ id: number; name: string }>> {
    const headers = await this.getAuthHeaders();
    try {
      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        `${BASE_AUTH_OFFICE_API}/actor/distributors`,
        { method: 'GET', headers },
        'ApiTokens',
        '/api-tokens/distributors'
      );
      const data = await response.json();
      const list: Array<{ id?: number; Id?: number; name?: string; Name?: string }> =
        data.distributors ?? data.Distributors ?? [];
      return list.map(d => ({ id: d.id ?? d.Id ?? 0, name: d.name ?? d.Name ?? '' }));
    } catch {
      return [];
    }
  }

  async getHierarchyActors(actorId: number | string): Promise<Array<{ actorId: number; name: string | null; parentActorId?: number | null; parentActorName?: string | null }>> {
    const authHeaders = await this.getAuthHeaders();
    const { selectedOperator } = useOperatorStore.getState();
    const headers: Record<string, string> = { ...authHeaders };
    if (selectedOperator?.operatorId) {
      headers['X-Operator-Id'] = String(selectedOperator.operatorId);
    }
    try {
      const response = await fetch(
        `${BASE_AUTH_OFFICE_API_V2}/actor/${actorId}/hierarchy?includeChildren=true`,
        { method: 'GET', headers }
      );
      if (!response.ok) return [];
      const data = await response.json();
      return data.actors ?? data.Actors ?? [];
    } catch {
      return [];
    }
  }

  async resolveActorNames(ids: number[]): Promise<Record<number, string>> {
    if (!ids.length) return {};
    const authHeaders = await this.getAuthHeaders();
    const { selectedOperator } = useOperatorStore.getState();
    const headers: Record<string, string> = { ...authHeaders };
    if (selectedOperator?.operatorId) {
      headers['X-Operator-Id'] = String(selectedOperator.operatorId);
    }

    const names: Record<number, string> = {};
    await Promise.all(ids.map(async (id) => {
      try {
        const response = await fetch(
          `${BASE_AUTH_OFFICE_API_V2}/actor/${id}/hierarchy?includeChildren=true`,
          { method: 'GET', headers }
        );
        if (!response.ok) return;
        const data = await response.json();
        const actors: Array<{ parentActorId?: number | null; parentActorName?: string | null }> =
          data.actors ?? data.Actors ?? [];
        const match = actors.find(a => a.parentActorId === id);
        if (match?.parentActorName) {
          names[id] = match.parentActorName;
        }
      } catch {
        // ignore individual failures
      }
    }));
    return names;
  }
}
